/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useSaveWithAction.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Reusable hook for handling save actions with dropdown menu.
    Manages save-and-close, save-and-new, and save actions.
────────────────────────────────────────────────────────────────*/

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SaveAction } from '../components/ui/SaveButtonWithDropdown';

interface UseSaveWithActionOptions {
  onSave: () => Promise<any>; // Returns saved data or undefined
  listPath: string; // Path to navigate to list (e.g., '/pbx/applications/conferences')
  newPath: string; // Path to navigate to new form (e.g., '/pbx/applications/conferences/new')
  mode: 'create' | 'edit';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getEditPath?: (savedData: any) => string; // Optional: function to get edit path from saved data
  getFormData?: () => any; // Optional: function to get current form data for "Save and New"
  storageKey?: string; // Optional: key for sessionStorage (e.g., 'extensions:saveAndNewData')
}

/**
 * Hook for handling save actions with dropdown menu
 * Returns a handler function that can be passed to FixedActionBar's onSaveWithAction
 */
export function useSaveWithAction({
  onSave,
  listPath,
  newPath,
  mode,
  getEditPath,
  getFormData,
  storageKey,
}: UseSaveWithActionOptions) {
  const navigate = useNavigate();

  const handleSaveWithAction = useCallback(
    async (action: SaveAction) => {
      try {
        const savedData = await onSave();

        if (!savedData) {
          return; // Save failed or was cancelled
        }

        // Handle different actions after save
        if (action === 'save-and-close') {
          // Navigate back to list
          navigate(listPath, { replace: true });
        } else if (action === 'save-and-new') {
          // Clear any saved data to ensure a fresh blank form
          if (storageKey) {
            try {
              if (getFormData) {
                // Save current form state to preserve sticky fields like Class of Service
                sessionStorage.setItem(storageKey, JSON.stringify(getFormData()));
              } else {
                sessionStorage.removeItem(storageKey);
              }
              // Set a timestamp to force reset when navigating to new form
              // This ensures the form resets even when navigating to the same route
              sessionStorage.setItem(storageKey + ':reset', Date.now().toString());
            } catch (error) {
              console.error('Error handling saved form data:', error);
            }
          }
          // Add a small delay to ensure save completes and state updates before navigation
          setTimeout(() => {
            // Add a timestamp query parameter to force React Router to treat this as a new navigation
            navigate(`${newPath}?_reset=${Date.now()}`, { replace: true });
          }, 100);
        } else if (action === 'save') {
          // If we just created a new item, navigate to edit mode
          // Check if we were in create mode by checking if savedData has an id and we don't have a current edit path
          // This handles the case where mode might not be updated yet
          if (savedData.id && (!mode || mode === 'create')) {
            if (getEditPath) {
              navigate(getEditPath(savedData), { replace: true });
            } else {
              // Default: append ID to list path
              navigate(`${listPath}/${savedData.id}`, { replace: true });
            }
          }
          // If editing, just stay in the form (no navigation needed)
        }
      } catch (error) {
        // Error already handled in onSave
      }
    },
    [onSave, navigate, listPath, newPath, mode, getEditPath, getFormData, storageKey]
  );

  return handleSaveWithAction;
}

